import { AdaptiveTestLevel, AdaptiveTestStatus } from "services/adaptiveTest/model";
import { Plan, SessionPlatform } from "services/sessions/model";
import { SubscriptionPlan } from "../subscriptions/model";
import { StudentTeam } from "services/teams/model";

export type Student = {
  id: string;
  last_seen: string | null;
  status: StudentStatus;
  score: StudentScore;
  level: StudentLevel | null;
  adaptive_test: {
    status: AdaptiveTestStatus;
    evaluated_level: AdaptiveTestLevel | null;
    start_level: AdaptiveTestLevel;
    id: string;
  } | null;
  profile: StudentProfile;
  avatar: string;
  primary_instructor: StudentPrimaryInstructor | null;
  preferred_platform: SessionPlatform;
  plans: Plan[];
  number_of_lessons: number;
  learning_reasons: string | null;
  things_of_interest: string | null;
  industry: string | null;
  credits: Credits;
  // FIXME should be called can_purchase as it considers self-study plans too
  can_purchase_lessons: boolean;
  has_purchases: boolean;
  type: StudentType;
  weekly_activity_goal: number | null;
  daily_activity_goal: number | null;
  has_pro_access: boolean;
  has_all_onboarding_data: boolean;
  subscription_plan: SubscriptionPlan | null;
  onboarding_status: StudentOnboardingStatus;
  access: StudentAccess;
};

export type AdminStudent = Student & {
  company?: AdminStudentCompany | null;
  instructor?: AdminStudentInstructor | null;
  roles?: string[];
  created: string;
  student?: {
    learning_reasons: string | null;
    level: StudentLevel | null;
    id: string;
    name: string;
    score_range: number[];
    value: number;
    number_of_lessons: number;
    preferred_platform: SessionPlatform;
    status: StudentStatus;
    type: StudentType;
  };
};

type AdminStudentCompany = {
  id: string;
  name: string;
  teams: StudentTeam[];
};

type AdminStudentInstructor = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  last_seen: string | null;
  timezone: { id: string; utc_offset: string };
};

export type StudentType = "one-on-one" | "group" | "self-study";

export const STUDENT_STATUSES = [
  "paying",
  "graduated",
  "paused",
  "pilot",
  "pilot-completed",
  "setup",
] as const;
export type StudentStatus = (typeof STUDENT_STATUSES)[number];

export type StudentPrimaryInstructor = {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
};

export type StudentAccess = {
  pro_content: boolean;
  adaptive_test: boolean;
  practice_exercises: boolean;
  speaking_club: boolean;
  dictionary: boolean;
};

export function isStudentType(value?: string): value is StudentType {
  return (
    value !== undefined && ["one-on-one", "group", "self-study"].indexOf(value) !== -1
  );
}

export type StudentProfile = {
  first_name: string;
  last_name: string | null;
  email: string | null;
  city: string | null;
  country: string | null;
  timezone: { id: string; utc_offset: string };
};

/**
 * A student as the places that hand out work know them. The roster nests the name under
 * `profile`, a recently-used student carries it at the top level, and the placeholder
 * option in an assign dropdown carries only an id, so all three have to be readable here.
 */
export type AssignableStudent = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  profile?: { first_name?: string | null; last_name?: string | null };
};

export type StudentOnboardingStatus = {
  has_native_language: boolean;
  has_industry: boolean;
  has_title: boolean;
  has_level: boolean;
  has_goals: boolean;
  has_timezone?: boolean;
  has_learning_reasons?: boolean;
  has_availability?: boolean;
  has_management_level?: boolean;
  has_job_category?: boolean;
};

export type StudentLevel = {
  id: string;
  name: string;
  value: number;
  score_range: [number, number];
};

export type StudentScore = {
  idioms: number;
  eloquence: number;
  intonation: number;
  familiarity: number;
  pronunciation: number;
  grammar_variety: number;
  intelligibility: number;
  grammar_accuracy: number;
  vocabulary_variety: number;
  vocabulary_accuracy: number;
};

// [DEPRECATED] For use by legacy JS modules.
export const StudentStatusEnum = {
  PAYING: "paying",
  PILOT: "pilot",
  PILOT_COMPLETED: "pilot-completed",
  GRADUATED: "graduated",
  PAUSED: "paused",
  SETUP: "setup",
} as const satisfies Record<string, StudentStatus>;

export type Credits = {
  ai_credits: number;
  ai_credits_on_hold: number;
  ai_credits_remaining: number;
  ai_credits_total: number;
  ai_credits_used: number;
  paid: number;
  free: number;
  remaining_annual_ss_licenses: number;
  remaining_monthly_ss_licenses: number;
  completed: number;
  forfeited: number;
  scheduled: number;
  remaining: number;
  allocated?: number;
  unallocated?: number;
};
